import { TopNav } from "@dashboard/components/AppLayout";
import { DashboardCard } from "@dashboard/components/Card";
import { ListPageLayout } from "@dashboard/components/Layouts";
import { getApiUrl, getAppPlatformUrl } from "@dashboard/config";
import {
  postConfigurationUpdate,
  REDACTED_SECRET_PLACEHOLDER,
  withRedactedPlaceholders,
} from "@dashboard/envSettings/services/configurationApi";
import useNotifier from "@dashboard/hooks/useNotifier";
import { commonMessages } from "@dashboard/intl";
import { Box, Button, Input, Spinner, Switch, Text } from "@saleor/macaw-ui-next";
import React, { useEffect, useState } from "react";
import { useIntl } from "react-intl";

interface SMTPConfig {
  user: string;
  email: string;
  port: string;
  host: string;
  password: string;
  layer: "SSL" | "TLS";
  _redacted?: string[];
}

export const SMTP: React.FC = () => {
  const intl = useIntl();
  const notify = useNotifier();
  const [isActive, setIsActive] = useState(false);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [config, setConfig] = useState<SMTPConfig>({
    user: "",
    email: "",
    port: "465",
    host: "",
    password: "",
    layer: "SSL",
  });

  const updateConfiguration = async (activeStatus: boolean, configurations: SMTPConfig) => {
    setLoading(true);

    try {
      const tenant = getApiUrl();

      const response = await postConfigurationUpdate({
        tenant: tenant,
        app_name: "smtp",
        configurations: {
          user: configurations.user,
          email: configurations.email,
          port: configurations.port,
          host: configurations.host,
          password: configurations.password,
          layer: configurations.layer,
          _redacted: configurations._redacted,
        },
        is_active: activeStatus,
      });

      if (!response.ok) {
        throw new Error(`Failed to update configuration: ${response.statusText}`);
      }

      setIsActive(activeStatus);
      notify({
        status: "success",
        text: intl.formatMessage(commonMessages.savedChanges),
      });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to update configuration";

      notify({
        status: "error",
        text: errorMessage,
      });
      setIsActive(!activeStatus);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleChange = (value: string) => {
    const newActiveStatus = value === "active";

    updateConfiguration(newActiveStatus, config);
  };

  const handleSaveConfiguration = () => {
    updateConfiguration(isActive, config);
  };

  const handleInputChange = (field: keyof SMTPConfig, value: string) => {
    setConfig(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleLayerChange = (value: string) => {
    setConfig(prev => ({
      ...prev,
      layer: value as "SSL" | "TLS",
    }));
  };

  // Load existing configuration from API
  useEffect(() => {
    const loadConfiguration = async () => {
      try {
        const tenant = getApiUrl();

        if (!tenant) {
          setIsActive(false);
          setInitialLoading(false);

          return;
        }

        const response = await fetch(
          `${getAppPlatformUrl("app-configurations")}/app/get-configuration?tenant=${encodeURIComponent(tenant)}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
            },
          },
        );

        if (response.ok) {
          const result = await response.json();

          // Look for SMTP app configuration
          const smtpApp = result.find((app: any) => app.app_name === "smtp");

          if (smtpApp) {
            setIsActive(smtpApp.is_active !== false);

            if (smtpApp.configurations) {
              const redactedFields = Array.isArray(smtpApp._redacted)
                ? smtpApp._redacted
                : smtpApp.configurations._redacted || [];
              const configurations = withRedactedPlaceholders(
                smtpApp.configurations,
                redactedFields,
              );

              setConfig({
                user: configurations.user || "",
                email: configurations.email || "",
                port: configurations.port || "465",
                host: configurations.host || "",
                password: configurations.password || "",
                layer: configurations.layer || "SSL",
                _redacted: configurations._redacted,
              });
            }
          } else {
            setIsActive(false);
          }
        } else {
          setIsActive(false);
        }
      } catch (error) {
        setIsActive(false);
      } finally {
        setInitialLoading(false);
      }
    };

    loadConfiguration();
  }, []);

  if (initialLoading) {
    return (
      <ListPageLayout>
        <TopNav withoutBorder title="SMTP Configuration" />
        <DashboardCard paddingX={6}>
          <Box paddingY={6}>
            <Box display="flex" flexDirection="column" gap={4}>
              <Box>
                <Text size={5} fontWeight="bold" marginBottom={2}>
                  SMTP Configuration
                </Text>
                <Text size={3} color="default2">
                  Configure SMTP settings for email delivery.
                </Text>
              </Box>
              <Box
                padding={4}
                borderWidth={1}
                borderColor="default1"
                borderStyle="solid"
                borderRadius={4}
                display="flex"
                alignItems="center"
                justifyContent="center"
                gap={3}
                __minHeight="80px"
              >
                <Spinner />
                <Text size={3} color="default2">
                  Loading configuration...
                </Text>
              </Box>
            </Box>
          </Box>
        </DashboardCard>
      </ListPageLayout>
    );
  }

  return (
    <ListPageLayout>
      <TopNav withoutBorder title="SMTP Configuration" />
      <DashboardCard paddingX={6}>
        <Box paddingY={6}>
          <Box display="flex" flexDirection="column" gap={4}>
            <Box>
              <Text size={5} fontWeight="bold" marginBottom={2}>
                SMTP Configuration
              </Text>
              <Text size={3} color="default2">
                Configure SMTP settings for email delivery.
              </Text>
            </Box>

            {/* Active/Inactive Toggle */}
            <Box
              padding={4}
              borderWidth={1}
              borderColor="default1"
              borderStyle="solid"
              borderRadius={4}
            >
              <Box display="flex" flexDirection="column" gap={3}>
                <Box display="flex" alignItems="center" gap={3}>
                  <Text size={4} fontWeight="medium">
                    Enable SMTP
                  </Text>
                  {loading ? (
                    <Box display="flex" alignItems="center" gap={2}>
                      <Spinner />
                      <Text size={3} color="default2">
                        Updating...
                      </Text>
                    </Box>
                  ) : (
                    <Switch
                      value={isActive ? "active" : "inactive"}
                      onValueChange={handleToggleChange}
                      disabled={loading}
                      __height="32px"
                      __width="fit-content"
                      padding={0.5}
                    >
                      <Switch.Item
                        id="inactive"
                        value="inactive"
                        name="status"
                        disabled={loading}
                        marginRight={0.5}
                      >
                        <Box
                          display="flex"
                          justifyContent="center"
                          alignItems="center"
                          paddingX={2}
                          height="100%"
                        >
                          <Text size={2} fontWeight="medium">
                            Off
                          </Text>
                        </Box>
                      </Switch.Item>
                      <Switch.Item
                        id="active"
                        value="active"
                        name="status"
                        disabled={loading}
                        marginLeft={0.5}
                      >
                        <Box
                          display="flex"
                          justifyContent="center"
                          alignItems="center"
                          paddingX={2}
                          height="100%"
                        >
                          <Text size={2} fontWeight="medium">
                            On
                          </Text>
                        </Box>
                      </Switch.Item>
                    </Switch>
                  )}
                </Box>
                <Text size={3} color="default2">
                  Enable or disable SMTP email delivery
                </Text>
              </Box>
            </Box>

            {/* Configuration Fields */}
            <Box
              padding={4}
              borderWidth={1}
              borderColor="default1"
              borderStyle="solid"
              borderRadius={4}
            >
              <Box display="flex" flexDirection="column" gap={4}>
                <Text size={4} fontWeight="medium">
                  SMTP Settings
                </Text>

                {/* User */}
                <Box display="flex" flexDirection="column" gap={1}>
                  <Text size={3} fontWeight="medium">
                    User
                  </Text>
                  <Input
                    type="text"
                    value={config.user}
                    onChange={e => handleInputChange("user", e.target.value)}
                    disabled={loading}
                  />
                </Box>

                {/* Email */}
                <Box display="flex" flexDirection="column" gap={1}>
                  <Text size={3} fontWeight="medium">
                    Email
                  </Text>
                  <Input
                    type="email"
                    value={config.email}
                    onChange={e => handleInputChange("email", e.target.value)}
                    disabled={loading}
                  />
                </Box>

                {/* Host */}
                <Box display="flex" flexDirection="column" gap={1}>
                  <Text size={3} fontWeight="medium">
                    Host
                  </Text>
                  <Input
                    type="text"
                    value={config.host}
                    onChange={e => handleInputChange("host", e.target.value)}
                    disabled={loading}
                    placeholder="e.g., smtp.gmail.com"
                  />
                </Box>

                {/* Port */}
                <Box display="flex" flexDirection="column" gap={1}>
                  <Text size={3} fontWeight="medium">
                    Port
                  </Text>
                  <Input
                    type="text"
                    value={config.port}
                    onChange={e => handleInputChange("port", e.target.value)}
                    disabled={loading}
                    placeholder="e.g., 465 for SSL, 587 for TLS"
                  />
                </Box>

                {/* Password */}
                <Box display="flex" flexDirection="column" gap={1}>
                  <Text size={3} fontWeight="medium">
                    Password
                  </Text>
                  <Input
                    type="password"
                    value={config.password}
                    onChange={e => handleInputChange("password", e.target.value)}
                    disabled={loading}
                    placeholder={
                      config._redacted?.includes("password")
                        ? REDACTED_SECRET_PLACEHOLDER
                        : undefined
                    }
                  />
                </Box>

                {/* Layer (SSL/TLS) */}
                <Box display="flex" flexDirection="column" gap={2}>
                  <Text size={3} fontWeight="medium">
                    Security Layer
                  </Text>
                  <Switch
                    value={config.layer}
                    onValueChange={handleLayerChange}
                    disabled={loading}
                    __height="32px"
                    __width="fit-content"
                    padding={0.5}
                  >
                    <Switch.Item
                      id="ssl"
                      value="SSL"
                      name="layer"
                      disabled={loading}
                      marginRight={0.5}
                    >
                      <Box
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        paddingX={2}
                        height="100%"
                      >
                        <Text size={2} fontWeight="medium">
                          SSL
                        </Text>
                      </Box>
                    </Switch.Item>
                    <Switch.Item
                      id="tls"
                      value="TLS"
                      name="layer"
                      disabled={loading}
                      marginLeft={0.5}
                    >
                      <Box
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        paddingX={2}
                        height="100%"
                      >
                        <Text size={2} fontWeight="medium">
                          TLS
                        </Text>
                      </Box>
                    </Switch.Item>
                  </Switch>
                  <Text size={2} color="default2">
                    SSL typically uses port 465, TLS typically uses port 587
                  </Text>
                </Box>

                {/* Save Button */}
                <Box display="flex" justifyContent="flex-end" marginTop={2}>
                  <Button variant="primary" onClick={handleSaveConfiguration} disabled={loading}>
                    {loading ? "Saving..." : "Save Configuration"}
                  </Button>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </DashboardCard>
    </ListPageLayout>
  );
};
